import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { SnackbarProvider } from 'notistack';
import { store } from './store/store';
import { theme } from './styles/theme';
import { AuthProvider, useAuth } from './hooks/useAuth';
import MainLayout from './components/layout/MainLayout';

// Lazy-loaded pages
const Login = React.lazy(() => import('./components/layout/Login'));
const Register = React.lazy(() => import('./components/onboarding/Register'));
const Dashboard = React.lazy(() => import('./components/dashboard/Dashboard'));
const ComplianceExplorer = React.lazy(() => import('./components/compliance/ComplianceExplorer'));
const AssessmentForm = React.lazy(() => import('./components/compliance/AssessmentForm'));
const EvidenceManager = React.lazy(() => import('./components/evidence/EvidenceManager'));
const GapDashboard = React.lazy(() => import('./components/compliance/GapDashboard'));
const CapaBoard = React.lazy(() => import('./components/capa/CapaBoard'));
const AmspRounds = React.lazy(() => import('./components/amsp/AmspRounds'));
const KpiDashboard = React.lazy(() => import('./components/kpi/KpiDashboard'));
const TemplateLibrary = React.lazy(() => import('./components/templates/TemplateLibrary'));
const ReportsPage = React.lazy(() => import('./components/reports/ReportsPage'));
const UserManagement = React.lazy(() => import('./components/users/UserManagement'));
const Settings = React.lazy(() => import('./components/settings/Settings'));
const OnboardingWizard = React.lazy(() => import('./components/onboarding/OnboardingWizard'));
const Iterations = React.lazy(() => import('./components/compliance/Iterations'));
const SelfAssessment = React.lazy(() => import('./components/compliance/SelfAssessment'));
const SentinelEvents = React.lazy(() => import('./components/sentinel/SentinelEvents'));
const NotificationCenter = React.lazy(() => import('./components/notifications/NotificationCenter'));

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
};

const App: React.FC = () => (
  <Provider store={store}>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SnackbarProvider maxSnack={3} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <BrowserRouter>
          <AuthProvider>
            <Suspense fallback={<div style={{display:'flex',justifyContent:'center',alignItems:'center',height:'100vh'}}>Loading GenBioCa NABH Platform...</div>}>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
                  <Route index element={<Dashboard />} />
                  <Route path="dashboard" element={<Dashboard />} />
                  <Route path="compliance/iterations" element={<Iterations />} />
                  <Route path="compliance/self-assessment" element={<SelfAssessment />} />
                  <Route path="compliance/*" element={<ComplianceExplorer />} />
                  <Route path="assessment/:oeId" element={<AssessmentForm />} />
                  <Route path="evidence" element={<EvidenceManager />} />
                  <Route path="gaps" element={<GapDashboard />} />
                  <Route path="capa" element={<CapaBoard />} />
                  <Route path="amsp/*" element={<AmspRounds />} />
                  <Route path="kpi" element={<KpiDashboard />} />
                  <Route path="templates" element={<TemplateLibrary />} />
                  <Route path="reports" element={<ReportsPage />} />
                  <Route path="sentinel" element={<SentinelEvents />} />
                  <Route path="notifications" element={<NotificationCenter />} />
                  <Route path="users" element={<UserManagement />} />
                  <Route path="settings" element={<Settings />} />
                  <Route path="onboarding" element={<OnboardingWizard />} />
                </Route>
              </Routes>
            </Suspense>
          </AuthProvider>
        </BrowserRouter>
      </SnackbarProvider>
    </ThemeProvider>
  </Provider>
);

export default App;
